/**
 * Landing Page - AlgoSolve
 * Hero, Features, Categories, How it Works, FAQ
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categoryService } from '../services/api';
import './LandingPage.css';

// ─── Data ────────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: '🔍', title: 'Interactive Dry Runs', desc: 'Watch algorithms execute step-by-step with animated visualizations. Pause, rewind, and replay at your own speed.' },
  { icon: '📝', title: 'Solve Problems', desc: 'Practice with hand-crafted problems across all algorithm categories. Write code directly in the browser.' },
  { icon: '⚖️', title: 'Compare Algorithms', desc: 'Place two algorithms side by side. Compare complexity, behavior, and trade-offs visually.' },
  { icon: '📚', title: 'Deep Explanations', desc: 'Every algorithm comes with pseudocode, advantages, disadvantages, and real-world applications.' },
  { icon: '🔖', title: 'Bookmarks', desc: 'Bookmark algorithms for quick access later. Your data stays in your browser, no account needed.' },
  { icon: '🚀', title: 'No Sign Up', desc: 'Start learning immediately. No accounts, no emails, no paywalls. Just open and learn.' },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Browse Categories', desc: 'Explore 6 OS unit categories covering all major algorithm families from scheduling to file systems.' },
  { step: '02', title: 'Study the Algorithm', desc: 'Read the explanation, pseudocode, complexity analysis, and real-world use cases.' },
  { step: '03', title: 'Watch the Dry Run', desc: 'Press play and watch the algorithm execute on sample input step-by-step.' },
  { step: '04', title: 'Solve Problems', desc: 'Apply what you learned. Write JavaScript solutions and run them against test cases.' },
];

const FAQS = [
  { q: 'Do I need to create an account?', a: 'No! AlgoSolve is completely free to use without any registration. Just visit the site and start learning.' },
  { q: 'What algorithms are covered?', a: 'We cover all 6 units of Operating Systems: process scheduling, synchronization, deadlock, memory management, disk scheduling, and file systems.' },
  { q: 'What language do I code in?', a: 'The problem solver uses JavaScript, which runs safely in the browser. This allows instant feedback without any server-side execution.' },
  { q: 'Can I bookmark algorithms?', a: 'Yes! Use the ☆ bookmark button on any algorithm card. Bookmarks are saved to your browser\'s localStorage.' },
  { q: 'How does the dry run work?', a: 'Each algorithm has a pre-computed step-by-step trace. You can play, pause, step forward/back, and control the speed.' },
  { q: 'Can I compare algorithms?', a: 'Yes! Visit the Compare page to select any two algorithms and see their details side by side.' },
];

const STATS = [
  { value: '15+', label: 'Algorithms' },
  { value: '6', label: 'OS Units' },
  { value: '6', label: 'Practice Problems' },
  { value: '0', label: 'Sign-ups Needed' },
];

// ─── Component ────────────────────────────────────────────────────────────────
function LandingPage() {
  const [categories, setCategories] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    categoryService.getAll()
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  return (
    <div className="landing">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          <div className="hero-grid" />
        </div>

        <div className="container hero-content">
          <div className="hero-eyebrow">
            <span className="eyebrow-dot" />
            OS Algorithm Learning Platform
          </div>

          <h1 className="hero-title">
            Learn. Visualize.
            <br />
            <span className="hero-gradient">Master Algorithms.</span>
          </h1>

          <p className="hero-desc">
            Interactive step-by-step dry runs, in-depth explanations, and hands-on
            problem solving for every major OS algorithm. No login required.
          </p>

          <div className="hero-cta">
            <Link to="/algorithms" className="btn btn-primary btn-lg">
              Explore Algorithms →
            </Link>
            <Link to="/problems" className="btn btn-secondary btn-lg">
              Solve Problems
            </Link>
          </div>

          {/* Stats */}
          <div className="hero-stats">
            {STATS.map(s => (
              <div key={s.label} className="stat">
                <span className="stat-value">{s.value}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section className="section features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Features</span>
            <h2>Everything you need to master OS algorithms</h2>
            <p>Built for students, by developers. Clean, fast, and completely free.</p>
          </div>

          <div className="features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="feature-card card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Categories ───────────────────────────────────────────────────── */}
      <section className="section categories-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Categories</span>
            <h2>Algorithm Categories</h2>
            <p>Organized by OS curriculum units for structured learning</p>
          </div>

          <div className="categories-grid">
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/algorithms?category=${cat.id}`}
                className="cat-card card card-clickable"
                style={{ '--cat-color': cat.color }}
              >
                <div className="cat-icon" style={{ background: `${cat.color}22`, color: cat.color }}>
                  {cat.icon}
                </div>
                <div className="cat-body">
                  <div className="cat-meta">
                    <span className="cat-unit">{cat.unit}</span>
                    <span className="cat-count">{cat.algorithmCount} algorithms</span>
                  </div>
                  <h3 className="cat-name">{cat.name}</h3>
                  <p className="cat-desc">{cat.description}</p>
                </div>
                <div className="cat-arrow" style={{ color: cat.color }}>→</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ─────────────────────────────────────────────────── */}
      <section className="section how-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">How It Works</span>
            <h2>From zero to mastery in 4 steps</h2>
          </div>

          <div className="how-grid">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={step.step} className="how-card">
                <div className="how-step-num">{step.step}</div>
                {i < HOW_IT_WORKS.length - 1 && <div className="how-connector" />}
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Problem Solving Preview ───────────────────────────────────────── */}
      <section className="section preview-section">
        <div className="container">
          <div className="preview-inner">
            <div className="preview-text">
              <span className="section-tag">Problem Solving</span>
              <h2>Code. Test. Learn.</h2>
              <p>
                Write real JavaScript solutions to algorithm problems. Run your code
                against test cases and see instant feedback on correctness.
              </p>
              <ul className="preview-list">
                <li>✓ Monaco code editor with syntax highlighting</li>
                <li>✓ Pre-defined test cases for each problem</li>
                <li>✓ Instant run & compare expected vs actual output</li>
                <li>✓ Starter code to help you begin</li>
                <li>✓ Hints when you're stuck</li>
              </ul>
              <Link to="/problems" className="btn btn-primary" style={{ marginTop: 24, display: 'inline-flex' }}>
                Browse Problems →
              </Link>
            </div>

            <div className="preview-window">
              <div className="window-bar">
                <span className="dot dot-red" />
                <span className="dot dot-yellow" />
                <span className="dot dot-green" />
                <span className="window-title">fcfs-calculator.js</span>
              </div>
              <pre className="preview-code">{`function solve(input) {
  const lines = input.split('\\n');
  const n = parseInt(lines[0]);
  const processes = [];

  for (let i = 1; i <= n; i++) {
    const [id, arrival, burst] =
      lines[i].trim().split(' ');
    processes.push({
      id,
      arrival: +arrival,
      burst: +burst
    });
  }

  // Sort by arrival time
  processes.sort((a, b) =>
    a.arrival - b.arrival);

  let time = 0, totalWait = 0;
  for (const p of processes) {
    if (time < p.arrival) time = p.arrival;
    totalWait += time - p.arrival;
    time += p.burst;
  }

  return (totalWait / n).toFixed(2);
}

return solve(input);`}</pre>
              <div className="window-output">
                <span className="output-label">Output:</span>
                <span className="output-val text-green">3.33</span>
                <span className="output-badge">✓ All tests passed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="section faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">FAQ</span>
            <h2>Frequently Asked Questions</h2>
          </div>

          <div className="faq-list">
            {FAQS.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <span className="faq-toggle">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="faq-a">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────── */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-inner">
            <h2>Ready to master OS algorithms?</h2>
            <p>No sign up. No credit card. Just learning.</p>
            <div className="hero-cta">
              <Link to="/algorithms" className="btn btn-primary btn-lg">
                Start Learning Now →
              </Link>
              <Link to="/problems" className="btn btn-secondary btn-lg">
                Go to Problems
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
